/**
 * Admin Settings Page
 * Central administration settings including scheduled reports, system config, etc.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, Calendar, Settings, Users, Shield, Bell, Database
} from "lucide-react";
import ScheduledReportsPanel from "@/components/ScheduledReportsPanel";

export default function AdminSettingsPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState("reports");

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-white" : "bg-gray-50 text-gray-900"}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? "border-slate-800 bg-slate-900" : "border-gray-200 bg-white"}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold flex items-center gap-2">
                  <Settings className="w-6 h-6 text-teal-500" />
                  Admin Settings
                </h1>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  System configuration and administration
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`mb-6 ${isDark ? "bg-slate-800" : ""}`}>
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Scheduled Reports
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              User Management
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Security
            </TabsTrigger>
          </TabsList>

          {/* Scheduled Reports Tab */}
          <TabsContent value="reports">
            <ScheduledReportsPanel isDark={isDark} />
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardHeader>
                <CardTitle>Notification Settings</CardTitle>
                <CardDescription>Configure system notifications and alerts</CardDescription>
              </CardHeader>
              <CardContent>
                <p className={isDark ? "text-gray-400" : "text-gray-600"}>
                  Notification settings coming soon...
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users">
            <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
                <CardDescription>Manage user accounts and permissions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  <Button onClick={() => navigate('/user-management')}>
                    <Users className="w-4 h-4 mr-2" />
                    Open User Management
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardHeader>
                <CardTitle>Security Settings</CardTitle>
                <CardDescription>Configure security and access controls</CardDescription>
              </CardHeader>
              <CardContent>
                <p className={isDark ? "text-gray-400" : "text-gray-600"}>
                  Security settings coming soon...
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
